/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_PRESENTATION, GET_SECTIONS, SECTION_OPTION } from "../graphql";
import { VApplication } from "../../../application";

const log: any = LoggerModule?.getInstance?.();

export interface ISectionOptionTypeMethods extends IPluginMethodMap {
  getSectionOptionProps(args?: any): Promise<any>;
}

export class SectionOptionTypePageService implements IAgentPlugin {
  readonly methods: ISectionOptionTypeMethods;
  private staticPageContentConfig: any;

  constructor(staticPageContentConfig: any = {}) {
    this.staticPageContentConfig = staticPageContentConfig;

    this.methods = {
      getSectionOptionProps: this.getSectionOptionProps.bind(this),
    };
  }

  async getSectionOptionProps(args: any = {}) {
    const app = VApplication.getInstance();
    const graphqlModule = app.getSync(
      "services.GraphqlServerModule",
    ) as GraphqlServerModule;
    const client = graphqlModule?.getClient?.("graphql-gateway");

    try {
      const getId = (item: any): string | undefined => {
        if (!item) return undefined;
        if (typeof item === "string") return item;

        return item?.id ?? item?._id;
      };

      const unwrapList = (value: any): any[] => {
        if (!value) return [];

        if (Array.isArray(value)) return value;

        if (Array.isArray(value?.content)) return value.content;
        if (Array.isArray(value?.data)) return value.data;
        if (Array.isArray(value?.items)) return value.items;
        if (Array.isArray(value?.nodes)) return value.nodes;
        if (Array.isArray(value?.results)) return value.results;

        return [value];
      };

      const normalizeStatus = (status: any): string => {
        return String(status ?? "")
          .trim()
          .toLowerCase();
      };

      const normalizeId = (value: any): string | undefined => {
        if (!value) return undefined;
        if (typeof value === "string") return value;

        return value?.id ?? value?._id;
      };

      const normalizeImages = (
        images?: any,
        img1?: any,
        img2?: any,
      ): string[] => {
        const result: string[] = [];

        if (Array.isArray(images)) {
          images.forEach((image) => {
            if (typeof image === "string" && image) {
              result.push(image);
            }

            if (typeof image === "object" && image?.base64) {
              result.push(image.base64);
            }
          });
        }

        if (typeof img1 === "string" && img1) {
          result.push(img1);
        }

        if (typeof img2 === "string" && img2) {
          result.push(img2);
        }

        return Array.from(new Set(result.filter(Boolean)));
      };

      const getPresentationPayload = (data: any): any => {
        return (
          data?.getPresentation ??
          data?.getPresentations ??
          data?.presentations ??
          data?.presentation ??
          null
        );
      };

      const getCostumeTypePayload = (data: any): any => {
        return (
          data?.getCostumeTypes ??
          data?.costumeTypes ??
          data?.sectionOptions ??
          null
        );
      };

      const buildPresentationFilter = () => {
        if (args?.presentationFilter) return args.presentationFilter;
        if (args?.filter) return args.filter;

        return {};
      };

      if (!client) {
        return {
          isEmpty: true,
          activePresentation: null,
          presentation: null,
          presentationId: undefined,
          sectionId: undefined,
          sectionOptions: [],
          staticPageContentConfig: this.staticPageContentConfig ?? {},
          error: "GraphQL client not found.",
        };
      }
      console.log(args);

      const activePresentationId =
        args?.presentationId ||
        args?.id ||
        args?.location?.state?.presentationId ||
        args?.location?.state?.id;

      if (!activePresentationId) {
        return {
          isEmpty: true,
          presentationId: undefined,
          sectionId: undefined,
          sectionOptions: [],
          staticPageContentConfig: this.staticPageContentConfig ?? {},
          message: "Presentation id not found.",
        };
      }

      console.log("presentationId:", activePresentationId);
      if (!activePresentationId) {
        return {
          isEmpty: true,
          activePresentation: null,
          presentation: null,
          presentationId: undefined,
          sectionId: undefined,
          sectionOptions: [],
          staticPageContentConfig: this.staticPageContentConfig ?? {},
          message: "No active presentation found.",
        };
      }
      console.log(activePresentationId);
      const costumeTypeRes = await client.query({
        query: SECTION_OPTION,
        variables: {
          filter: {
            presentation: {
              eq: activePresentationId,
            },
          },
        },
        fetchPolicy: "network-only",
      });

      const costumeTypePayload = getCostumeTypePayload(costumeTypeRes?.data);
      const costumeTypes = unwrapList(costumeTypePayload);

      /*
       * Step 4:
       * Find section id.
       *
       * Your getCostumeTypes query does not return section at root level.
       * It returns section inside costumeFeatures, so we use that as fallback.
       */
      const firstFeatureSectionId = (() => {
        for (const costumeType of costumeTypes || []) {
          for (const feature of costumeType?.costumeFeatures || []) {
            const sectionId = normalizeId(feature?.section);
            if (sectionId) return sectionId;
          }
        }

        return undefined;
      })();

      const sectionId =
        normalizeId(args?.sectionId) ??
        normalizeId(args?.section) ??
        firstFeatureSectionId;

      /*
       * Step 5:
       * Format data for TreeAccordion.
       *
       * TreeAccordion expects:
       *
       * sectionOptions -> costumeFeatures -> costumeAttributes -> costumeValues
       *
       * We keep backend fields as-is and add parent ids:
       * presentation, section, costumeType, costumeFeature, costumeAttribute.
       */
      const sectionOptions = (costumeTypes || []).map((costumeType: any) => {
        const costumeTypeId = getId(costumeType);

        const costumeTypeSectionId =
          normalizeId(costumeType?.section) ??
          normalizeId(costumeType?.costumeFeatures?.[0]?.section) ??
          sectionId;

        return {
          ...costumeType,
          id: costumeTypeId,
          name: costumeType?.name ?? "Untitled Section Option Type",
          presentation: costumeType?.presentation ?? activePresentationId,
          section: costumeTypeSectionId,
          images: normalizeImages(
            costumeType?.images,
            costumeType?.img1,
            costumeType?.img2,
          ),

          costumeFeatures: (costumeType?.costumeFeatures || []).map(
            (feature: any) => {
              console.log(feature);
              const featureId = getId(feature);
              const featureSectionId = Array.isArray(feature?.section)
                ? feature.section
                    .map((item: any) => item?.id ?? item?._id)
                    .filter(Boolean)
                : [];
              // normalizeId(feature?.section) ?? costumeTypeSectionId;
              console.log(featureSectionId);
              return {
                ...feature,
                id: featureId,
                name: feature?.name ?? "Untitled Feature",
                presentation:
                  feature?.presentation ??
                  costumeType?.presentation ??
                  activePresentationId,
                section: featureSectionId,
                sections: featureSectionId?.length > 0 ? featureSectionId : [],
                costumeType: feature?.costumeType ?? costumeTypeId,
                images: normalizeImages(
                  feature?.images,
                  feature?.img1,
                  feature?.img2,
                ),

                costumeAttributes: (feature?.costumeAttributes || []).map(
                  (attribute: any) => {
                    const attributeId = getId(attribute);

                    return {
                      ...attribute,
                      id: attributeId,
                      name: attribute?.name ?? "Untitled Attribute",
                      presentation:
                        attribute?.presentation ??
                        feature?.presentation ??
                        costumeType?.presentation ??
                        activePresentationId,
                      section:
                        normalizeId(attribute?.section) ?? featureSectionId,
                      costumeFeature: attribute?.costumeFeature ?? featureId,
                      images: normalizeImages(
                        attribute?.images,
                        attribute?.img1,
                        attribute?.img2,
                      ),

                      costumeValues: (attribute?.costumeValues || []).map(
                        (value: any) => {
                          return {
                            ...value,
                            id: getId(value),
                            name: value?.name ?? "Untitled Value",
                            presentation:
                              value?.presentation ??
                              attribute?.presentation ??
                              feature?.presentation ??
                              costumeType?.presentation ??
                              activePresentationId,
                            section:
                              normalizeId(value?.section) ?? featureSectionId,
                            costumeAttribute:
                              value?.costumeAttribute ?? attributeId,
                          };
                        },
                      ),
                    };
                  },
                ),
              };
            },
          ),
        };
      });
      const sectionsRes = await client.query({
        query: GET_SECTIONS,
        variables: {
          filter: {
            presentation: {
              eq: activePresentationId,
            },
          },
        },
        fetchPolicy: "network-only",
      });

      const sections = unwrapList(sectionsRes?.data?.getSections);
      return {
        isEmpty: sectionOptions.length === 0,
        presentationId: activePresentationId,
        sectionId,
        sectionOptions,
        sections,
        staticPageContentConfig: this.staticPageContentConfig ?? {},
      };
    } catch (err: any) {
      log?.error?.("error in getSectionOptionProps", err);
      console.log("error in getSectionOptionProps", err);

      return {
        isEmpty: true,
        activePresentation: null,
        presentation: null,
        presentationId: undefined,
        sectionId: undefined,
        sectionOptions: [],
        staticPageContentConfig: this.staticPageContentConfig ?? {},
        error: err?.message ?? err,
      };
    }
  }
}
